import { useParams, useNavigate } from "react-router-dom";
import { useMoviesStore } from "../../stores/useMoviesStore";
import { useFirebaseStore } from "../../stores/useFirebaseStore";
import "./index.css";
import { getFormattedDate } from "../../utils/date";
import { useEffect, useState, useCallback } from "react";
import { THEME_COLOR } from "../../utils/colors";
import Loader from "../../components/common/Loader";
import { useMovieItem } from "../../components/movie-item/useMovieItem";
import { showShare } from "../../components/common/ShareDialog";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ShareIcon from "@mui/icons-material/Share";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LocalMoviesIcon from "@mui/icons-material/LocalMovies";
import MovieCast from "./components/movie-cast";
import MovieRecommendations from "./components/movie-recommendations";
import MovieWatchProviders from "./components/movie-watch-providers";
import { logMovieCTA } from "../../service/analytics";

export default function MovieDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    movieDetail,
    fetchMovieDetail,
    isLoading,
    resetMovieDetail,
    movieImages,
    fetchMovieImages,
    recommendedMovies,
    fetchRecommendedMovies,
    movieCast,
    fetchMovieCast,
    watchProviders,
    fetchMovieWatchProviders,
  } = useMoviesStore();

  const { updateMovieInWatchList } = useFirebaseStore();

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const { getMovieOptions, onOptionClick } = useMovieItem(movieDetail);

  const {
    poster_path,
    backdrop_path,
    title,
    vote_average,
    release_date,
    trailer,
    overview,
    tagline,
    genres,
    status,
  } = movieDetail ?? {};

  const imagePath = poster_path ?? backdrop_path;

  const carouselImages =
    movieImages && movieImages.length > 0
      ? movieImages.map((img) => img.file_path)
      : imagePath
        ? [imagePath]
        : [];

  const openFullscreen = (index: number) => {
    setCurrentImageIndex(index);
    setIsFullscreen(true);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
  };

  const nextImage = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      setCurrentImageIndex((prev) =>
        prev === carouselImages.length - 1 ? 0 : prev + 1,
      );
    },
    [carouselImages.length],
  );

  const prevImage = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      setCurrentImageIndex((prev) =>
        prev === 0 ? carouselImages.length - 1 : prev - 1,
      );
    },
    [carouselImages.length],
  );

  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEndX(null);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return;
    const distance = touchStartX - touchEndX;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      prevImage();
    }
  };

  useEffect(() => {
    const movieId = parseInt(id!);
    fetchMovieDetail({ id: movieId });
    fetchMovieImages({ id: movieId });
    fetchRecommendedMovies({ id: movieId });
    fetchMovieCast({ id: movieId });
    fetchMovieWatchProviders({ id: movieId });
    window.scrollTo(0, 0);
    return () => {
      resetMovieDetail();
    };
  }, [
    id,
    fetchMovieDetail,
    fetchMovieImages,
    fetchRecommendedMovies,
    fetchMovieCast,
    fetchMovieWatchProviders,
    resetMovieDetail,
  ]);

  useEffect(() => {
    if (movieDetail) {
      updateMovieInWatchList(movieDetail);
    }
  }, [movieDetail, updateMovieInWatchList]);

  // Keyboard navigation
  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeFullscreen();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, nextImage, prevImage]);


  if (isLoading && movieDetail === null) {
    return <Loader fullScreen />;
  }

  if (movieDetail === null) {
    return <div className="not-found-container">Movie not found!</div>;
  }

  const onTrailerPress = () => {
    if (movieDetail) {
      logMovieCTA("cta_click_trailer", movieDetail);
    }
    if (!trailer) return;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const youtubeId = trailer.split("v=")[1];

    if (isMobile && youtubeId) {
      const protocol = /iPhone|iPad|iPod/i.test(navigator.userAgent)
        ? "youtube://"
        : "intent://";
      const deepLink = `${protocol}www.youtube.com/watch?v=${youtubeId}`;

      window.location.href = deepLink;
      setTimeout(() => {
        window.location.href = trailer;
      }, 500);
    } else {
      window.open(trailer, "_blank", "noopener,noreferrer");
    }
  };

  const onSharePress = () => {
    if (movieDetail) {
      logMovieCTA("cta_click_share", movieDetail, { share_platform: "dialog" });
      showShare(movieDetail);
    }
  };


  const movieOptions = getMovieOptions().filter(
    (opt) => opt.type !== "cancel" && opt.type !== "share",
  );

  return (
    <div className="Detail">
      <div className="detail-header-row">
        <button
          className="back-button"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ArrowBackIcon style={{ color: THEME_COLOR }} />
          <span>Back to Movies</span>
        </button>
      </div>
      <div className="detail-content-layout">
        <div className="detail-left-section">
          <div className="container">
            <div className="image-section">
              {imagePath ? (
                <div
                  className="single-image-container"
                  onClick={() => openFullscreen(0)}
                >
                  <img
                    src={`${process.env.REACT_APP_RAPID_IMAGE_BASE_URL}/w500/${imagePath}`}
                    alt={title}
                    className="single-image"
                  />
                  <button
                    className="fullscreen-trigger-btn"
                    aria-label="View Fullscreen"
                  >
                    <FullscreenIcon />
                  </button>
                </div>
              ) : (
                <div
                  className="compact-movie-placeholder"
                  style={{
                    width: "100%",
                    aspectRatio: "2/3",
                    borderRadius: 16,
                  }}
                >
                  <LocalMoviesIcon sx={{ fontSize: 48, color: "#94a3b8" }} />
                </div>
              )}
            </div>

            <div className="details-section">
              <h1 className="movie-title">{title}</h1>
              {tagline && <p className="movie-tagline">"{tagline}"</p>}

              <div className="meta-row">
                {release_date && (
                  <span className="meta-tag release-tag">
                    Released {getFormattedDate(release_date)}
                  </span>
                )}
                {status && status !== "Released" && (
                  <span className="meta-tag status-tag">{status}</span>
                )}
                {vote_average > 0 && (
                  <div className="rating-pill">
                    <img
                      className="imdb-logo"
                      alt="IMDB logo"
                      src={require("../../assets/ic_imdb.png")}
                    />
                    <span className="rating-value">
                      {vote_average.toFixed(1)}
                    </span>
                    <span className="rating-scale">/10</span>
                  </div>
                )}
              </div>

              {genres && genres.length > 0 && (
                <div className="genres-list">
                  {genres.map((genre) => (
                    <span key={genre.id} className="genre-badge">
                      {genre.name}
                    </span>
                  ))}
                </div>
              )}

              <div className="action-buttons">
                {trailer && (
                  <button
                    onClick={onTrailerPress}
                    className="action-btn trailer-btn"
                  >
                    <PlayArrowIcon />
                    Trailer
                  </button>
                )}
                {movieOptions.map((option) => {
                  const isSelected = option.title.includes("Remove");
                  return (
                    <button
                      key={option.type}
                      onClick={() => onOptionClick(option.type)}
                      className={`action-btn ${option.type} ${isSelected ? "selected" : ""}`}
                    >
                      {option.icon}
                      <span>{option.title}</span>
                    </button>
                  );
                })}
                <button onClick={onSharePress} className="action-btn share-btn">
                  <ShareIcon />
                  Share
                </button>
              </div>

              {overview && (
                <div className="overview-container">
                  <h3 className="section-title">Overview</h3>
                  <p className="overview-text">{overview}</p>
                </div>
              )}

              {/* Watch Providers Section */}
              <MovieWatchProviders
                watchProviders={watchProviders}
                movieTitle={title || ""}
                movieDetail={movieDetail}
              />
            </div>
          </div>

          {/* Recommended Movies Section */}
          <MovieRecommendations recommendedMovies={recommendedMovies} />

          <MovieCast cast={movieCast} />
        </div>
      </div>

      {isFullscreen && carouselImages.length > 0 && (
        <div className="fullscreen-overlay" onClick={closeFullscreen}>
          <button
            className="fullscreen-close-btn"
            onClick={closeFullscreen}
            aria-label="Close fullscreen"
          >
            <CloseIcon sx={{ fontSize: 28 }} />
          </button>

          {carouselImages.length > 1 && (
            <button
              className="fullscreen-nav-btn left"
              onClick={prevImage}
              aria-label="Previous image"
            >
              <ChevronLeftIcon sx={{ fontSize: 36 }} />
            </button>
          )}

          <div
            className="fullscreen-content"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={`${process.env.REACT_APP_RAPID_IMAGE_BASE_URL}/w780/${carouselImages[currentImageIndex]}`}
              alt={`${title} - ${currentImageIndex + 1}`}
              className="fullscreen-image"
            />
            {carouselImages.length > 1 && (
              <div className="fullscreen-counter">
                {currentImageIndex + 1} / {carouselImages.length}
              </div>
            )}

            {/* Prefetch adjacent images */}
            {carouselImages.length > 1 && (
              <>
                <img
                  src={`${process.env.REACT_APP_RAPID_IMAGE_BASE_URL}/w780/${carouselImages[currentImageIndex === 0 ? carouselImages.length - 1 : currentImageIndex - 1]}`}
                  style={{ display: "none" }}
                  alt="preload-prev"
                />
                <img
                  src={`${process.env.REACT_APP_RAPID_IMAGE_BASE_URL}/w780/${carouselImages[currentImageIndex === carouselImages.length - 1 ? 0 : currentImageIndex + 1]}`}
                  style={{ display: "none" }}
                  alt="preload-next"
                />
              </>
            )}
          </div>

          {carouselImages.length > 1 && (
            <button
              className="fullscreen-nav-btn right"
              onClick={nextImage}
              aria-label="Next image"
            >
              <ChevronRightIcon sx={{ fontSize: 36 }} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
